import { collection, addDoc, getDocs, getDoc, doc, deleteDoc, updateDoc, query, orderBy, where } from 'firebase/firestore';
import { db, auth } from '../services/firebase-config';

// Collection name in Firestore
const STORIES_COLLECTION = 'stories';

/**
 * Saves a story to Firebase Firestore
 * @param {Object} story - The story object to save
 * @returns {Promise<string>} - The ID of the saved story
 */
export async function saveStory(story) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to save a story');
    }
    
    // Add userId to the story object
    const storyWithUserId = {
      ...story,
      userId: auth.currentUser.uid,
      createdAt: new Date().toISOString()
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, STORIES_COLLECTION), storyWithUserId);
    console.log('[firebase-stories] Story saved with ID:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('[firebase-stories] Error saving story:', error);
    throw error;
  }
}

/**
 * Retrieves all stories for the current user
 * @returns {Promise<Array>} - Array of story objects with their IDs
 */
export async function getUserStories() {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to retrieve stories');
    }
    
    // Create query for user's stories ordered by creation date
    const q = query(
      collection(db, STORIES_COLLECTION),
      where('userId', '==', auth.currentUser.uid),
      orderBy('createdAt', 'desc')
    );
    
    // Execute query
    const querySnapshot = await getDocs(q);
    
    // Map results to array with IDs
    const stories = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return stories;
  } catch (error) {
    console.error('[firebase-stories] Error retrieving stories:', error);
    throw error;
  }
}

/**
 * Retrieves a single story by ID
 * @param {string} storyId - The ID of the story to retrieve
 * @returns {Promise<Object|null>} - The story object or null if not found
 */
export async function getStoryById(storyId) {
  try {
    // Get document reference
    const storyRef = doc(db, STORIES_COLLECTION, storyId);
    
    // Get document
    const storySnap = await getDoc(storyRef);
    
    // Check if exists and belongs to current user
    if (!storySnap.exists()) {
      console.warn(`[firebase-stories] Story with ID ${storyId} not found`);
      return null;
    }
    
    const storyData = storySnap.data();
    
    // Security check: ensure story belongs to current user
    if (storyData.userId !== auth.currentUser?.uid) {
      console.error('[firebase-stories] Attempt to access story belonging to another user');
      return null;
    }
    
    // Return story with ID
    return {
      id: storyId,
      ...storyData
    };
  } catch (error) {
    console.error('[firebase-stories] Error retrieving story by ID:', error);
    throw error;
  }
}

/**
 * Deletes a story by ID
 * @param {string} storyId - The ID of the story to delete
 * @returns {Promise<boolean>} - True if deletion was successful
 */
export async function deleteStory(storyId) {
  try {
    // First check if story exists and belongs to user
    const story = await getStoryById(storyId);
    
    if (!story) {
      throw new Error('Story not found or access denied');
    }
    
    // Delete the document
    await deleteDoc(doc(db, STORIES_COLLECTION, storyId));
    console.log(`[firebase-stories] Story with ID ${storyId} deleted`);
    
    return true;
  } catch (error) {
    console.error('[firebase-stories] Error deleting story:', error);
    throw error;
  }
}

/**
 * Updates an existing story
 * @param {string} storyId - The ID of the story to update
 * @param {Object} updatedStory - The updated story data
 * @returns {Promise<boolean>} - True if update was successful
 */
export async function updateStory(storyId, updatedStory) {
  try {
    // First check if story exists and belongs to user
    const existingStory = await getStoryById(storyId);
    
    if (!existingStory) {
      throw new Error('Story not found or access denied');
    }
    
    // Update the document
    await updateDoc(doc(db, STORIES_COLLECTION, storyId), updatedStory);
    console.log(`[firebase-stories] Story with ID ${storyId} updated`);
    
    return true;
  } catch (error) {
    console.error('[firebase-stories] Error updating story:', error);
    throw error;
  }
}
